//
// Copyright © 2026 ZeroDesk
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * Validators and formatters for German personnel master data.
 * All functions are pure and accept values with arbitrary spaces.
 */

function compact (value: string | undefined | null): string {
  return (value ?? '').replace(/[\s.-]/g, '').toUpperCase()
}

function digitSum (value: number): number {
  let sum = 0
  for (const ch of String(value)) sum += Number(ch)
  return sum
}

/**
 * Steuer-ID (IdNr): 11 digits, first digit not zero, check digit by ISO 7064 MOD 11,10.
 * Among the first ten digits exactly one digit appears two or three times, none appears more often and
 * no digit appears three times in a row.
 * @public
 */
export function validateSteuerId (value: string | undefined | null): boolean {
  const id = compact(value)
  if (!/^[1-9]\d{10}$/.test(id)) return false
  const digits = id.split('').map(Number)

  const counts = new Map<number, number>()
  for (const d of digits.slice(0, 10)) counts.set(d, (counts.get(d) ?? 0) + 1)
  const repeated = Array.from(counts.values()).filter((c) => c > 1)
  if (repeated.length !== 1 || repeated[0] > 3) return false
  for (let i = 0; i < 8; i++) {
    if (digits[i] === digits[i + 1] && digits[i] === digits[i + 2]) return false
  }

  let product = 10
  for (let i = 0; i < 10; i++) {
    let sum = (digits[i] + product) % 10
    if (sum === 0) sum = 10
    product = (sum * 2) % 11
  }
  let check = 11 - product
  if (check === 10) check = 0
  return check === digits[10]
}

const svWeights = [2, 1, 2, 5, 7, 1, 2, 1, 2, 1, 2, 7]

/**
 * Sozialversicherungsnummer (Rentenversicherungsnummer): 12 characters
 * AA DDMMYY B SS P (area, birth date, first letter of birth name, serial, check digit).
 * @public
 */
export function validateSvNummer (value: string | undefined | null): boolean {
  const sv = compact(value)
  const match = /^(\d{2})(\d{2})(\d{2})(\d{2})([A-Z])(\d{2})(\d)$/.exec(sv)
  if (match === null) return false
  const day = Number(match[2])
  const month = Number(match[3])
  if (day < 1 || day > 31 || month < 1 || month > 12) return false

  const letter = String(sv.charCodeAt(8) - 64).padStart(2, '0')
  const numeric = sv.slice(0, 8) + letter + sv.slice(9, 11)
  let sum = 0
  for (let i = 0; i < 12; i++) {
    sum += digitSum(Number(numeric[i]) * svWeights[i])
  }
  return sum % 10 === Number(sv[11])
}

/**
 * IBAN check by ISO 13616 (mod 97). German IBANs must have 22 characters.
 * @public
 */
export function validateIban (value: string | undefined | null): boolean {
  const iban = compact(value)
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false
  if (iban.startsWith('DE') && iban.length !== 22) return false
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let remainder = 0
  for (const ch of rearranged) {
    const code = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch
    for (const digit of code) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }
  return remainder === 1
}

/**
 * German postal code (five digits).
 * @public
 */
export function validatePlz (value: string | undefined | null): boolean {
  return /^\d{5}$/.test((value ?? '').trim())
}

/**
 * Month-based tax class value (I to VI).
 * @public
 */
export function isTaxClass (value: number | undefined | null): boolean {
  return value != null && Number.isInteger(value) && value >= 1 && value <= 6
}

/**
 * Groups characters by `size` for display.
 * @public
 */
export function formatGrouped (value: string | undefined | null, size = 4): string {
  const v = compact(value)
  const parts: string[] = []
  for (let i = 0; i < v.length; i += size) parts.push(v.slice(i, i + size))
  return parts.join(' ')
}

/**
 * @public
 */
export function formatIban (value: string | undefined | null): string {
  return formatGrouped(value, 4)
}

/**
 * Steuer-ID as `12 345 678 901`.
 * @public
 */
export function formatSteuerId (value: string | undefined | null): string {
  const v = compact(value)
  if (v.length !== 11) return v
  return `${v.slice(0, 2)} ${v.slice(2, 5)} ${v.slice(5, 8)} ${v.slice(8)}`
}

/**
 * SV number as `12 345678 A 901`-like groups (`AA DDMMYY B SSP`).
 * @public
 */
export function formatSvNummer (value: string | undefined | null): string {
  const v = compact(value)
  if (v.length !== 12) return v
  return `${v.slice(0, 2)} ${v.slice(2, 8)} ${v.slice(8, 9)} ${v.slice(9)}`
}

/**
 * Hides all but the last `visible` characters, e.g. `•••• 1234`.
 * @public
 */
export function maskValue (value: string | undefined | null, visible = 4): string {
  const v = compact(value)
  if (v === '') return ''
  if (v.length <= visible) return '•'.repeat(v.length)
  return `${'•'.repeat(Math.min(v.length - visible, 4))} ${v.slice(-visible)}`
}

/**
 * Next personnel number: highest numeric suffix of existing numbers plus one, keeping the prefix.
 * @public
 */
export function nextPersonnelNumber (existing: Array<string | undefined>, prefix = 'P', width = 4): string {
  let max = 0
  for (const num of existing) {
    const m = /(\d+)$/.exec(num ?? '')
    if (m !== null) max = Math.max(max, Number(m[1]))
  }
  return `${prefix}${String(max + 1).padStart(width, '0')}`
}
